import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useTypedSelector } from "../../redux/store";
import {
	getOrderPaymentStatus,
	paymentStatusColorKey,
	paymentStatusTextColor,
} from "../../utils/orderPaymentStatus";

const Badge = styled.span<{ background?: string; color?: string }>`
	display: inline-block;
	padding: 1px 8px;
	border-radius: 4px;
	line-height: 18px;
	white-space: nowrap;
	background: ${({ background }) => background ?? "transparent"};
	color: ${({ color }) => color ?? "inherit"};
`;

/**
 * The order payment status (card stage or payment link) on its color from
 * the color settings, as in the "Payment status" column.
 */
const OrderPaymentStatusBadge: React.FC<OrderPaymentStatusBadge.Props> = ({
	order,
	empty = null,
}) => {
	const { t } = useTranslation();
	const colors = useTypedSelector((state) => state.settings.colors);

	const status = getOrderPaymentStatus(order);
	if (!status) return <>{empty}</>;

	const background = colors[paymentStatusColorKey(status)];

	return (
		<Badge
			background={background}
			color={paymentStatusTextColor(background)}
		>
			{t(`order_payment_status.${status}`)}
		</Badge>
	);
};

declare namespace OrderPaymentStatusBadge {
	interface Props {
		order: Parameters<typeof getOrderPaymentStatus>[0];
		/** Rendered when the order has no payment status. */
		empty?: React.ReactNode;
	}
}

export default memo(OrderPaymentStatusBadge);
